import { useEffect, useState, useMemo } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";

function isExpirationKindSubmission(submission: any, task?: any): boolean {
  const taskKind = task?.taskKind || submission?.task?.taskKind;
  const informationType = task?.informationType || submission?.task?.informationType || submission?.informationType;
  return taskKind === 'expiration' || informationType === 'Expiration Date';
}

export default function Analytics() {
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<string>("all");
  const [sortColumn, setSortColumn] = useState<string>("total");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const handleSort = (col: string) => {
    if (sortColumn === col) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortColumn(col);
      setSortDir("desc");
    }
  };

  const [taskAnalytics, setTaskAnalytics] = useState<any[]>([]);

  // Filter states
  const [filterType, setFilterType] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterSubcategory, setFilterSubcategory] = useState<string>("all");
  const [filterItemType, setFilterItemType] = useState<string>("all");
  const [filterBounty, setFilterBounty] = useState<string>("all"); // "all", "bounty", "non-bounty"

  useEffect(() => {
    document.title = 'Analytics — Pixano';
  }, []);

  // Generate last 12 months for filter
  const getMonthOptions = () => {
    const months = [];
    const currentDate = new Date();
    
    // Add "All Time" option
    months.push({ value: "all", label: "All Time" });
    
    // Add last 12 months
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      months.push({ value, label });
    }
    
    return months;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      // Fetch all submissions
      const submissionsSnap = await getDocs(collection(db, "submissions"));
      const allSubmissions = submissionsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];
      setSubmissions(allSubmissions);

      // Fetch all tasks
      const tasksSnap = await getDocs(collection(db, "tasks"));
      const allTasks = tasksSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];
      setTasks(allTasks);

      setLoading(false);
    };
    fetchData();
  }, []);

  // Calculate task analytics based on selected month
  useEffect(() => {
    if (tasks.length === 0 || submissions.length === 0) return;

    const getLabelsCount = (labels: any) => {
      if (!labels) return 0;
      if (Array.isArray(labels)) return labels.length;
      if (typeof labels === 'object') return Object.keys(labels).length;
      return 0;
    };

    const calculateAnalytics = () => {
      // Apply filters to tasks first
      let filteredTasks = tasks;
      
      if (filterType !== "all") {
        filteredTasks = filteredTasks.filter((task: any) => task.type === filterType || task.taskType === filterType);
      }
      if (filterCategory !== "all") {
        filteredTasks = filteredTasks.filter((task: any) => task.category === filterCategory);
      }
      if (filterSubcategory !== "all") {
        filteredTasks = filteredTasks.filter((task: any) => task.subcategory === filterSubcategory);
      }
      if (filterItemType !== "all") {
        filteredTasks = filteredTasks.filter((task: any) => task.itemType === filterItemType);
      }
      if (filterBounty !== "all") {
        const isBounty = filterBounty === "bounty";
        filteredTasks = filteredTasks.filter((task: any) => {
          const taskBounty = task.bounty === true || task.bounty === "true";
          return isBounty ? taskBounty : !taskBounty;
        });
      }
      
      const analytics = filteredTasks.map((task: any) => {
        let taskSubmissions = submissions.filter((sub: any) => sub.taskId === task.id);
        
        // Filter by month if not "all"
        if (selectedMonth !== "all") {
          const [year, month] = selectedMonth.split('-').map(Number);
          taskSubmissions = taskSubmissions.filter((sub: any) => {
            const subDate = new Date(sub.submittedAt || sub.timestamp || sub.createdAt);
            return subDate.getFullYear() === year && subDate.getMonth() === month - 1;
          });
        }

        const approved = taskSubmissions.filter((sub: any) => sub.status === "approved").length;
        const rejected = taskSubmissions.filter((sub: any) => sub.status === "rejected").length;
        const pending = taskSubmissions.filter((sub: any) => sub.status === "pending").length;
        const flagged = taskSubmissions.filter((sub: any) => sub.status === "flagged").length;
        const total = taskSubmissions.length;

        // Data Packages: Match DatasetPackages.tsx logic exactly
        // - All submissions with status === 'completed'
        // - Submissions with status === 'approved' that were approved via Final Approval
        //   (identified by qaAction === 'final_approval' or qaFinalReviewReviewedAt existing)
        const dataPackagesCount = taskSubmissions.filter((sub: any) => {
          if (sub.status === 'completed') {
            return true; // Always include completed submissions
          }
          if (sub.status === 'approved') {
            // Include if it was approved via Final Approval
            return sub.qaAction === 'final_approval' || sub.qaFinalReviewReviewedAt != null;
          }
          return false;
        }).length;

        // Photo Packages: Match PhotoPackages.tsx logic exactly
        // - taskType === 'Photo'
        // - status === 'approved' OR status === 'completed'
        const photoPackagesCount = taskSubmissions.filter(
          (sub: any) =>
            sub.taskType === "Photo" &&
            (sub.status === "completed" || sub.status === "approved")
        ).length;

        // Total annotations (labels) across all submissions for this task
        const annotations = taskSubmissions.reduce(
          (sum: number, sub: any) => sum + getLabelsCount((sub as any).labels),
          0
        );

        // ---- Marketplace lane counts (mirrors the Dashboard's eligibility rules) ----
        let rawIn = 0;
        let rawOut = 0;
        let annotatedIn = 0;
        let annotatedOut = 0;
        taskSubmissions.forEach((sub: any) => {
          // Raw lane — rawPhoto for Photo tasks, rawVideo for Video tasks
          if (sub.taskType === "Photo") {
            const ready =
              sub.status === "approved" ||
              sub.status === "completed" ||
              sub.status === "labeled" ||
              sub.status === "needs_relabeling";
            let eligible = ready;
            if (eligible && isExpirationKindSubmission(sub, task)) {
              const pe = sub.packagingEligibility || {};
              eligible = !!pe.photoPackagesEligible && sub.itemTypeStage === "final";
            }
            if (eligible) {
              if (sub.marketplace?.rawPhoto) rawIn += 1;
              else rawOut += 1;
            }
          } else if (sub.taskType === "Video") {
            if (sub.trimmedVideoUrl && (sub.status === "approved" || sub.status === "completed")) {
              if (sub.marketplace?.rawVideo) rawIn += 1;
              else rawOut += 1;
            }
          }

          // Annotated lane
          if (sub.isDeleted !== true) {
            let annEligible = false;
            if (sub.status === "completed") {
              annEligible = true;
            } else if (sub.status === "approved") {
              annEligible = sub.qaAction === "final_approval" || sub.qaFinalReviewReviewedAt != null;
            }
            if (annEligible && isExpirationKindSubmission(sub, task)) {
              const pe = sub.packagingEligibility || {};
              annEligible = !!pe.datasetPackagesEligible;
            }
            if (annEligible) {
              if (sub.marketplace?.annotatedPhoto) annotatedIn += 1;
              else annotatedOut += 1;
            }
          }
        });

        // TODO: Wire these to real fields when available
        const challenges = 0;
        
        // Bounty: Count submissions that were completed/approved while the task had bounty enabled
        // Since we don't store historical bounty status on submissions, we check if the task
        // currently has bounty enabled and count all completed/approved submissions for that task
        const taskBountyEnabled = task.bounty === true || task.bounty === "true";
        const bounty = taskBountyEnabled 
          ? taskSubmissions.filter((sub: any) => 
              sub.status === "completed" || sub.status === "approved"
            ).length
          : 0;

        const totalRejected = rejected;
        const rejectRate = total > 0 ? ((rejected / total) * 100).toFixed(1) : "0";

        return {
          ...task,
          approved,
          rejected,
          pending,
          flagged,
          total,
          approvalRate: total > 0 ? ((approved / total) * 100).toFixed(1) : "0",
          dataPackages: dataPackagesCount,
          photoPackages: photoPackagesCount,
          annotations,
          challenges,
          bounty,
          totalRejected,
          rejectRate,
          rawIn,
          rawOut,
          annotatedIn,
          annotatedOut
        };
      });

      // Sort by total submissions (most active first)
      analytics.sort((a, b) => b.total - a.total);
      setTaskAnalytics(analytics);
    };

    calculateAnalytics();
  }, [tasks, submissions, selectedMonth, filterType, filterCategory, filterSubcategory, filterItemType, filterBounty]);

  // Get unique values for filters
  const uniqueTypes = Array.from(new Set(tasks.map((t: any) => t.type || t.taskType).filter(Boolean))).sort();
  const uniqueCategories = Array.from(new Set(tasks.map((t: any) => t.category).filter(Boolean))).sort();
  const uniqueSubcategories = Array.from(new Set(tasks.map((t: any) => t.subcategory).filter(Boolean))).sort();
  const uniqueItemTypes = Array.from(new Set(tasks.map((t: any) => t.itemType).filter(Boolean))).sort();

  const sortedAnalytics = useMemo(() => {
    const valueFor = (row: any, col: string) => {
      if (col === "rejectRate") return parseFloat(row.rejectRate) || 0;
      return row[col];
    };
    const rows = [...taskAnalytics];
    rows.sort((a, b) => {
      const av = valueFor(a, sortColumn);
      const bv = valueFor(b, sortColumn);
      let cmp: number;
      if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv;
      } else {
        cmp = String(av ?? "").localeCompare(String(bv ?? ""));
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [taskAnalytics, sortColumn, sortDir]);

  // Calculate totals from filtered analytics
  const totalSubmissions = taskAnalytics.reduce((sum, task) => sum + task.total, 0);
  const totalApproved = taskAnalytics.reduce((sum, task) => sum + task.approved, 0);
  const totalRejected = taskAnalytics.reduce((sum, task) => sum + task.totalRejected, 0);
  const totalBountyCompleted = taskAnalytics.reduce((sum, task) => sum + task.bounty, 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Task Data Analytics</h1>
        <div className="flex gap-2">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {getMonthOptions().map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Data Boxes */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-100 p-4 rounded">
          <div className="text-2xl font-bold text-blue-600">
            {totalSubmissions}
          </div>
          <div className="text-sm text-gray-600">Total Submissions</div>
        </div>
        <div className="bg-green-100 p-4 rounded">
          <div className="text-2xl font-bold text-green-600">
            {totalApproved}
          </div>
          <div className="text-sm text-gray-600">Total Approved</div>
        </div>
        <div className="bg-red-100 p-4 rounded">
          <div className="text-2xl font-bold text-red-600">
            {totalRejected}
          </div>
          <div className="text-sm text-gray-600">Total Rejected</div>
        </div>
        <div className="bg-purple-100 p-4 rounded">
          <div className="text-2xl font-bold text-purple-600">
            {totalBountyCompleted}
          </div>
          <div className="text-sm text-gray-600">Total Bounty Completed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded mb-6">
        <h2 className="text-lg font-semibold mb-3">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              {uniqueTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {uniqueCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
            <select
              value={filterSubcategory}
              onChange={(e) => setFilterSubcategory(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Subcategories</option>
              {uniqueSubcategories.map(sub => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
            <select
              value={filterItemType}
              onChange={(e) => setFilterItemType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Item Types</option>
              {uniqueItemTypes.map(item => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bounty</label>
            <select
              value={filterBounty}
              onChange={(e) => setFilterBounty(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Tasks</option>
              <option value="bounty">Bounty Tasks Only</option>
              <option value="non-bounty">Non-Bounty Tasks Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Task Analytics Table */}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border">
            <thead>
              <tr className="bg-gray-100">
                {[
                  { key: "title", label: "Task Title", left: true },
                  { key: "subcategory", label: "Subcategory", left: false },
                  { key: "itemType", label: "Item Type", left: false },
                  { key: "category", label: "Category", left: false },
                  { key: "total", label: "Submissions", left: false },
                  { key: "approved", label: "Approved", left: false },
                  { key: "totalRejected", label: "Rejected", left: false },
                  { key: "rejectRate", label: "Reject Rate", left: false },
                  { key: "bounty", label: "Bounty", left: false },
                  { key: "rawIn", label: "Raw In Market", left: false },
                  { key: "rawOut", label: "Raw Not In", left: false },
                  { key: "annotatedIn", label: "Annot. In Market", left: false },
                  { key: "annotatedOut", label: "Annot. Not In", left: false },
                  { key: "annotations", label: "Annotations", left: false },
                ].map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className={`p-2 border whitespace-nowrap cursor-pointer select-none hover:bg-gray-200 ${col.left ? "text-left" : "text-center"}`}
                  >
                    {col.label}
                    {sortColumn === col.key ? (sortDir === "desc" ? " ▼" : " ▲") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedAnalytics.map((task) => (
                <tr key={task.id} className="text-center hover:bg-gray-50">
                  <td className="p-2 border text-left">{task.title}</td>
                  <td className="p-2 border">{task.subcategory || '-'}</td>
                  <td className="p-2 border">{task.itemType || '-'}</td>
                  <td className="p-2 border">{task.category}</td>
                  <td className="p-2 border font-semibold">{task.total}</td>
                  <td className="p-2 border font-semibold text-green-600">{task.approved}</td>
                  <td className="p-2 border font-semibold text-red-600">{task.totalRejected}</td>
                  <td className="p-2 border">
                    <span className={`font-semibold ${parseFloat(task.rejectRate) >= 80 ? 'text-red-600' : parseFloat(task.rejectRate) >= 40 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {task.rejectRate}%
                    </span>
                  </td>
                  <td className="p-2 border font-semibold">{task.bounty}</td>
                  <td className="p-2 border font-semibold text-green-600">{task.rawIn}</td>
                  <td className="p-2 border font-semibold">{task.rawOut}</td>
                  <td className="p-2 border font-semibold text-green-600">{task.annotatedIn}</td>
                  <td className="p-2 border font-semibold">{task.annotatedOut}</td>
                  <td className="p-2 border font-semibold">{task.annotations}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {taskAnalytics.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No task data found for the selected period.
            </div>
          )}
        </div>
      )}
    </div>
  );
} 